// src/modules/pedidos/pages/RecuentoNuevo.js
//
// Crear un recuento eligiendo productos del catálogo de Finnegans.
// Navegás Categoría (serie) → Color → se despliegan los productos y elegís
// individualmente (o "seleccionar todos"). Cada producto trae su código de
// Finnegans (codUru), serie y color.

import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth, hasRol } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { MENU_ESTRUCTURA, MAPA_COLORES } from "../../ecommerce/pages/catalogoConfig";
import { crearRecuento } from "../services/recuentosFS";
import { TIRAS_POR_PAQUETE } from "../services/tirasPorPaquete";

export default function RecuentoNuevo() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const esAdmin = (profile?.rol || "").toLowerCase() === "admin";

  const [deposito, setDeposito] = useState(profile?.deposito || "R8");
  const [nombre, setNombre]   = useState("");
  const [motivo, setMotivo]   = useState("");
  const [padreAbierto, setPadreAbierto] = useState("Perfiles Aluminio");
  const [grupoAbierto, setGrupoAbierto] = useState(null); // { rama1, color }
  const [productosGrupo, setProductosGrupo] = useState([]);
  const [loadingGrupo, setLoadingGrupo] = useState(false);
  const [sel, setSel] = useState({}); // { codUru: {codUru, nombre, serie, color, tirasPorPaquete} }
  const [operarios, setOperarios] = useState([]);
  const [asignado, setAsignado]   = useState(null);
  const [error, setError]     = useState("");
  const [guardando, setGuardando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [todosProductos, setTodosProductos] = useState(null); // catálogo completo (cache)
  const [cargandoTodos, setCargandoTodos] = useState(false);
  const [verSinCat, setVerSinCat] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "users"));
        setOperarios(snap.docs.map(d => ({ uid: d.id, ...d.data() })).filter(u => {
          if (!hasRol(u, "operario", "encargado")) return false;
          if (deposito && u.deposito && u.deposito !== deposito) return false;
          return true;
        }));
      } catch (e) { console.error("[operarios]", e); }
    })();
  }, [deposito]);

  async function abrirGrupo(rama1, color) {
    const mismo = grupoAbierto && grupoAbierto.rama1 === rama1 && grupoAbierto.color === (color || null);
    if (mismo) { setGrupoAbierto(null); setProductosGrupo([]); return; }
    setGrupoAbierto({ rama1, color: color || null });
    setLoadingGrupo(true); setProductosGrupo([]); setError("");
    try {
      const filtros = [where("rama1", "==", rama1)];
      if (color) filtros.push(where("color", "==", color));
      const snap = await getDocs(query(collection(db, "catalogoProductos"), ...filtros));
      const prods = snap.docs.map(d => {
        const x = d.data(); const cod = x.codUru || d.id;
        return { codUru: cod, nombre: x.customerNo || x.descripcion || cod, serie: x.rama1 || rama1, color: x.color || color || "—", tirasPorPaquete: TIRAS_POR_PAQUETE[cod] || 0 };
      }).sort((a, b) => String(a.codUru).localeCompare(String(b.codUru)));
      setProductosGrupo(prods);
    } catch (e) { console.error(e); setError("No se pudieron cargar los productos"); }
    finally { setLoadingGrupo(false); }
  }

  function toggleProd(p) {
    setSel(prev => {
      const next = { ...prev };
      if (next[p.codUru]) delete next[p.codUru]; else next[p.codUru] = p;
      return next;
    });
  }
  function seleccionarTodos(on) {
    setSel(prev => {
      const next = { ...prev };
      productosGrupo.forEach(p => { if (on) next[p.codUru] = p; else delete next[p.codUru]; });
      return next;
    });
  }

  // Carga el catálogo completo una sola vez (para buscador + sin categoría)
  async function cargarTodos() {
    if (todosProductos || cargandoTodos) return;
    setCargandoTodos(true);
    try {
      const snap = await getDocs(collection(db, "catalogoProductos"));
      const list = snap.docs.map(d => {
        const x = d.data(); const cod = x.codUru || d.id;
        return { codUru: cod, nombre: x.customerNo || x.descripcion || cod, serie: x.rama1 || "", color: x.color || "—", tirasPorPaquete: TIRAS_POR_PAQUETE[cod] || 0 };
      });
      setTodosProductos(list);
    } catch (e) { console.error(e); setTodosProductos([]); }
    finally { setCargandoTodos(false); }
  }

  // Carga el catálogo cuando se empieza a buscar o se pide "sin categoría"
  useEffect(() => {
    if ((busqueda.trim().length >= 2 || verSinCat) && !todosProductos && !cargandoTodos) cargarTodos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda, verSinCat]);

  const normTxt = s => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const resultados = useMemo(() => {
    if (!todosProductos) return [];
    const q = normTxt(busqueda.trim());
    if (busqueda.trim().length >= 2) {
      return todosProductos.filter(p => normTxt(p.codUru).includes(q) || normTxt(p.nombre).includes(q)).slice(0, 80);
    }
    if (verSinCat) return todosProductos.filter(p => !p.serie).slice(0, 300);
    return [];
  }, [todosProductos, busqueda, verSinCat]);

  const seleccionados = Object.values(sel);
  const todosDelGrupoSel = productosGrupo.length > 0 && productosGrupo.every(p => sel[p.codUru]);

  async function crear() {
    if (seleccionados.length === 0) { setError("Elegí al menos un producto"); return; }
    if (!nombre.trim()) { setError("Poné un nombre al recuento"); return; }
    setError(""); setGuardando(true);
    try {
      const id = await crearRecuento({
        nombre: nombre.trim(), motivo, deposito,
        origen: "CATEGORIA", productos: seleccionados, asignadoA: asignado, profile,
      });
      navigate(`/recuentos/${id}`);
    } catch (e) {
      console.error(e); setError(e.message || "No se pudo crear el recuento"); setGuardando(false);
    }
  }

  const label = { fontSize: "12px", fontWeight: 600, color: "#64748b", display: "block", marginBottom: "5px" };
  const chip = (active) => ({
    padding: "5px 11px", borderRadius: "999px", cursor: "pointer", fontSize: "0.76rem", fontWeight: active ? 700 : 500,
    border: `1.5px solid ${active ? "#1d4ed8" : "#e2e8f0"}`, background: active ? "#eff6ff" : "#fff", color: active ? "#1d4ed8" : "#475569",
  });

  // Panel de productos del grupo abierto (se renderiza bajo la subcategoría activa)
  const PanelProductos = () => (
    <div style={{ marginTop: "8px", border: "1px solid #bfdbfe", borderRadius: "10px", background: "#f8fafc", padding: "10px" }}>
      {loadingGrupo ? (
        <p style={{ margin: 0, fontSize: "0.8rem", color: "#64748b" }}>Cargando productos…</p>
      ) : productosGrupo.length === 0 ? (
        <p style={{ margin: 0, fontSize: "0.8rem", color: "#94a3b8" }}>No hay productos clasificados para esta serie/color.</p>
      ) : (
        <>
          <button type="button" onClick={() => seleccionarTodos(!todosDelGrupoSel)}
            style={{ fontSize: "0.74rem", fontWeight: 700, color: "#1d4ed8", background: "none", border: "none", cursor: "pointer", marginBottom: "6px", padding: 0 }}>
            {todosDelGrupoSel ? "✕ Quitar todos" : "✓ Seleccionar todos"} ({productosGrupo.length})
          </button>
          <div style={{ display: "flex", flexDirection: "column", gap: "3px", maxHeight: "240px", overflowY: "auto" }}>
            {productosGrupo.map(p => {
              const checked = !!sel[p.codUru];
              return (
                <label key={p.codUru} style={{ display: "flex", alignItems: "center", gap: "9px", padding: "6px 8px", borderRadius: "7px", background: checked ? "#eff6ff" : "#fff", border: `1px solid ${checked ? "#bfdbfe" : "#e2e8f0"}`, cursor: "pointer" }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleProd(p)} />
                  <span style={{ fontWeight: 700, fontSize: "0.8rem", color: "#0f172a" }}>{p.codUru}</span>
                  <span style={{ fontSize: "0.78rem", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.nombre}</span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="container" style={{ paddingBottom: "90px", maxWidth: "680px" }}>
      <header className="topbar card" style={{ marginBottom: "16px" }}>
        <button onClick={() => navigate("/recuentos")} className="btn btn--ghost btn-sm">⬅ Volver</button>
        <div><h1 className="h1" style={{ margin: 0, fontSize: "17px" }}>Nuevo recuento</h1></div>
        <div />
      </header>

      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

        {/* 1 · Elegir productos */}
        <div className="card">
          <label style={label}>1 · Elegí los productos (categoría → color → productos)</label>

          {/* Buscador + productos sin categoría */}
          <div style={{ marginBottom: "12px" }}>
            <div style={{ display: "flex", gap: "6px" }}>
              <input className="input" value={busqueda} onFocus={cargarTodos}
                onChange={e => { setBusqueda(e.target.value); if (verSinCat) setVerSinCat(false); }}
                placeholder="🔍 Buscar producto por nombre o código" style={{ flex: 1 }} />
              <button type="button" onClick={() => { setVerSinCat(v => !v); setBusqueda(""); cargarTodos(); }}
                className="btn btn--ghost btn-sm" style={{ flexShrink: 0, background: verSinCat ? "#eff6ff" : undefined, color: verSinCat ? "#1d4ed8" : undefined }}>
                📂 Sin categoría
              </button>
            </div>

            {(busqueda.trim().length >= 2 || verSinCat) && (
              <div style={{ marginTop: "8px", border: "1px solid #bfdbfe", borderRadius: "10px", background: "#f8fafc", padding: "10px" }}>
                {cargandoTodos ? (
                  <p style={{ margin: 0, fontSize: "0.8rem", color: "#64748b" }}>Cargando catálogo…</p>
                ) : resultados.length === 0 ? (
                  <p style={{ margin: 0, fontSize: "0.8rem", color: "#94a3b8" }}>{verSinCat ? "No hay productos sin categoría." : "Sin resultados."}</p>
                ) : (
                  <>
                    <p style={{ margin: "0 0 6px", fontSize: "0.72rem", color: "#64748b" }}>{resultados.length} resultado{resultados.length !== 1 ? "s" : ""}{verSinCat ? " sin categoría" : ""}</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "3px", maxHeight: "260px", overflowY: "auto" }}>
                      {resultados.map(p => {
                        const checked = !!sel[p.codUru];
                        return (
                          <label key={p.codUru} style={{ display: "flex", alignItems: "center", gap: "9px", padding: "6px 8px", borderRadius: "7px", background: checked ? "#eff6ff" : "#fff", border: `1px solid ${checked ? "#bfdbfe" : "#e2e8f0"}`, cursor: "pointer" }}>
                            <input type="checkbox" checked={checked} onChange={() => toggleProd(p)} />
                            <span style={{ fontWeight: 700, fontSize: "0.8rem", color: "#0f172a" }}>{p.codUru}</span>
                            <span style={{ fontSize: "0.78rem", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.nombre}</span>
                            {p.serie ? <span style={{ marginLeft: "auto", flexShrink: 0, fontSize: "0.66rem", color: "#94a3b8" }}>{p.serie} · {p.color}</span> : null}
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {MENU_ESTRUCTURA.filter(p => p.subcategorias.length > 0).map(padre => {
              const abierto = padreAbierto === padre.nombre;
              return (
                <div key={padre.nombre} style={{ border: "1px solid #e2e8f0", borderRadius: "10px", overflow: "hidden" }}>
                  <button type="button" onClick={() => setPadreAbierto(abierto ? null : padre.nombre)}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: "#f8fafc", border: "none", cursor: "pointer", fontWeight: 700, fontSize: "0.85rem", color: "#0f172a" }}>
                    <span>{padre.nombre}</span><span style={{ color: "#94a3b8" }}>{abierto ? "▾" : "▸"}</span>
                  </button>
                  {abierto && (
                    <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: "12px" }}>
                      {padre.subcategorias.map(sub => {
                        const grupoEsDeEstaSub = grupoAbierto && grupoAbierto.rama1 === sub.nombre;
                        return (
                          <div key={sub.nombre}>
                            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#334155", marginBottom: "5px" }}>{sub.nombre}</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                              {sub.colores && sub.colores.length > 0 ? (
                                <>
                                  <button type="button" onClick={() => abrirGrupo(sub.nombre, null)} style={chip(grupoEsDeEstaSub && grupoAbierto.color === null)}>Todos los colores</button>
                                  {sub.colores.map(c => (
                                    <button key={c} type="button" onClick={() => abrirGrupo(sub.nombre, c)} style={chip(grupoEsDeEstaSub && grupoAbierto.color === c)}>
                                      <span style={{ display: "inline-block", width: "9px", height: "9px", borderRadius: "50%", background: MAPA_COLORES[c] || "#cbd5e1", border: "1px solid #cbd5e1", marginRight: "5px", verticalAlign: "middle" }} />
                                      {c}
                                    </button>
                                  ))}
                                </>
                              ) : (
                                <button type="button" onClick={() => abrirGrupo(sub.nombre, null)} style={chip(grupoEsDeEstaSub && grupoAbierto.color === null)}>Ver productos</button>
                              )}
                            </div>
                            {grupoEsDeEstaSub && <PanelProductos />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {seleccionados.length > 0 && (
            <div style={{ marginTop: "12px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "10px 12px" }}>
              <p style={{ margin: 0, fontSize: "0.82rem", fontWeight: 700, color: "#1d4ed8" }}>✓ {seleccionados.length} producto{seleccionados.length !== 1 ? "s" : ""} seleccionado{seleccionados.length !== 1 ? "s" : ""}</p>
            </div>
          )}
        </div>

        {/* 2 · Datos */}
        <div className="card" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <label style={label}>2 · Datos del recuento</label>
          <div>
            <label style={label}>Nombre</label>
            <input className="input" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Recuento Serie 20 Anolok" style={{ width: "100%" }} />
          </div>
          <div>
            <label style={label}>Motivo (opcional)</label>
            <input className="input" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ej: control previo al ingreso del contenedor" style={{ width: "100%" }} />
          </div>
          {esAdmin && (
            <div>
              <label style={label}>Depósito</label>
              <select className="input" value={deposito} onChange={e => setDeposito(e.target.value)} style={{ width: "100%" }}>
                <option value="R8">R8</option>
                <option value="ISABELA">ISABELA</option>
              </select>
            </div>
          )}
          <div>
            <label style={label}>Asignar a (opcional)</label>
            <select className="input" value={asignado?.uid || ""} style={{ width: "100%" }}
              onChange={e => {
                const op = operarios.find(o => o.uid === e.target.value);
                setAsignado(op ? { uid: op.uid, nombre: op.nombre || op.email || "Operario" } : null);
              }}>
              <option value="">— Lo cuento yo / sin asignar —</option>
              {operarios.map(o => (
                <option key={o.uid} value={o.uid}>{o.nombre || o.email}{hasRol(o, "encargado") ? " (encargado)" : ""}</option>
              ))}
            </select>
          </div>
        </div>

        {error && <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: "8px", padding: "10px 12px", color: "#dc2626", fontSize: "0.83rem", fontWeight: 600 }}>⚠️ {error}</div>}
      </div>

      <div className="cta-fija" style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 16px", background: "#fff", borderTop: "1px solid #e2e8f0" }}>
        <button onClick={crear} disabled={guardando || seleccionados.length === 0} className="btn btn--primary"
          style={{ width: "100%", padding: "14px", fontSize: "15px", fontWeight: 700, opacity: (seleccionados.length === 0 || guardando) ? 0.6 : 1 }}>
          {guardando ? "Creando…" : `Crear recuento${seleccionados.length ? ` (${seleccionados.length})` : ""}`}
        </button>
      </div>
    </div>
  );
}
